import { Badge } from '@/components/shadcn/badge'
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { cn } from '@/lib/utils'
import { IconCloseOutline } from '@icons/IconCloseOutline'
import { KeyboardEvent, useCallback, useMemo, useRef, useState } from 'react'
import { Control, FieldPath, FieldValues } from 'react-hook-form'

interface StringListFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>
  name: TName
  label: string
  description?: string
  placeholder?: string
  className?: string
  disabled?: boolean
  normalizeEntry?: (raw: string) => string | null
}

export function StringListField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  description,
  placeholder = 'Type and press Enter to add',
  className,
  disabled = false,
  normalizeEntry,
}: StringListFieldProps<TFieldValues, TName>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <StringListFieldInner
          value={Array.isArray(field.value) ? field.value : []}
          onChange={field.onChange}
          onBlur={field.onBlur}
          label={label}
          description={description}
          placeholder={placeholder}
          className={className}
          disabled={disabled}
          normalizeEntry={normalizeEntry}
        />
      )}
    />
  )
}

type StringListFieldInnerProps = {
  value: string[]
  onChange: (value: string[]) => void
  onBlur: () => void
  label: string
  description?: string
  placeholder: string
  className?: string
  disabled: boolean
  normalizeEntry?: (raw: string) => string | null
}

function StringListFieldInner({
  value,
  onChange,
  onBlur,
  label,
  description,
  placeholder,
  className,
  disabled,
  normalizeEntry,
}: StringListFieldInnerProps) {
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const entries = useMemo(() => value, [value])

  const addEntry = useCallback(
    (raw: string) => {
      const trimmed = raw.trim()
      if (!trimmed) {
        setInputValue('')
        return
      }

      const entry = normalizeEntry ? normalizeEntry(trimmed) : trimmed
      if (!entry || entries.includes(entry)) {
        setInputValue('')
        return
      }

      onChange([...entries, entry])
      setInputValue('')
    },
    [entries, normalizeEntry, onChange]
  )

  const removeEntry = useCallback(
    (index: number) => {
      const updated = [...entries]
      updated.splice(index, 1)
      onChange(updated)
    },
    [entries, onChange]
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        addEntry(inputValue)
      }
      if (event.key === 'Backspace' && inputValue === '' && entries.length > 0) {
        removeEntry(entries.length - 1)
      }
    },
    [addEntry, entries.length, inputValue, removeEntry]
  )

  return (
    <FormItem className={cn(className)}>
      <FormLabel>{label}</FormLabel>
      {description ? <FormDescription>{description}</FormDescription> : null}
      <FormControl>
        <div
          className={cn(
            'ring-offset-offwhite flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
            disabled && 'cursor-not-allowed opacity-50'
          )}
          onClick={() => inputRef.current?.focus()}
        >
          {entries.map((entry, index) => (
            <Badge key={`${entry}-${index}`} variant="secondary" className="gap-1 py-0.5 pr-1">
              <span className="max-w-[200px] truncate">{entry}</span>
              {!disabled && (
                <button
                  type="button"
                  className="ring-offset-offwhite ml-0.5 rounded-full outline-none hover:bg-muted focus:ring-2 focus:ring-ring focus:ring-offset-1"
                  onClick={(event) => {
                    event.stopPropagation()
                    removeEntry(index)
                  }}
                >
                  <IconCloseOutline className="size-3" />
                </button>
              )}
            </Badge>
          ))}
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              if (inputValue.trim()) {
                addEntry(inputValue)
              }
              onBlur()
            }}
            disabled={disabled}
            placeholder={entries.length === 0 ? placeholder : 'Add more...'}
            className="h-auto min-w-[120px] flex-1 border-0 bg-transparent p-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
      </FormControl>
      <FormMessage />
    </FormItem>
  )
}
